// Loading skeletons.
//
// A screen that is waiting shows the shape of what is coming rather than the
// word "Loading": the tiles, the chart, the table and its columns, drawn at the
// size they will really be. Two reasons, both practical. Nothing moves when the
// data lands, so a figure cannot be misread because the row shifted under the
// cursor. And the shape itself says what to expect — four tiles and a table is
// a different promise from a chart.
//
// Every skeleton here is built from the same primitives as the real screen, so
// a column added to a table is a column added to its skeleton rather than a
// separate drawing that quietly drifts out of step.

import { type ReactNode } from 'react';
import { cx, Table, Th, Td, TableWrap, Tiles } from './ui';

/**
 * One block. Screen readers are told nothing about it — the region announces
 * itself once, in Loading, rather than every bone announcing itself.
 */
export function Bone(
  { w, h = 13, radius, className }:
  { w?: number | string; h?: number | string; radius?: number; className?: string },
) {
  return (
    <span aria-hidden="true" className={cx('skeleton', className)}
          style={{ width: w ?? '100%', height: h, borderRadius: radius }} />
  );
}

/**
 * The region a skeleton fills.
 *
 * `aria-busy` marks it as in flight and the label is read once. Without this a
 * screen reader meets a silent page with no way to know anything is coming.
 */
export function Loading({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div role="status" aria-busy="true" aria-live="polite">
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/** Lines of prose. The last is short, the way a wrapped paragraph ends. */
export function Lines({ n = 2, className }: { n?: number; className?: string }) {
  return (
    <div className={cx('flex flex-col gap-2', className)}>
      {Array.from({ length: n }, (_, i) => (
        <Bone key={i} h={11} w={i === n - 1 ? '48%' : i % 2 ? '88%' : '96%'} />
      ))}
    </div>
  );
}

// ── page furniture ────────────────────────────────────────────

/** Mirrors PageHead: eyebrow, title, dek, and the rule beneath them. */
export function HeadSkeleton({ dek = true, actions }: { dek?: boolean; actions?: number }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 border-b border-rule pb-4 mb-6">
      <div className="min-w-0 flex-1">
        <Bone w={130} h={9} />
        <Bone w="min(420px, 70%)" h={27} className="mt-2.5" />
        {dek && <Bone w="min(560px, 90%)" h={12} className="mt-3" />}
      </div>
      {actions ? (
        <div className="flex flex-wrap items-center gap-2">
          {Array.from({ length: actions }, (_, i) => <Bone key={i} w={96} h={34} radius={6} />)}
        </div>
      ) : null}
    </div>
  );
}

/** Mirrors Tiles and Tile, including the hero's larger figure. */
export function TilesSkeleton({ n = 4, hero = true }: { n?: number; hero?: boolean }) {
  return (
    <Tiles>
      {Array.from({ length: n }, (_, i) => (
        <div key={i} className="bg-card p-4 pb-5 min-w-0">
          <Bone w={i === 0 ? 104 : 84} h={9} className="mb-2.5" />
          <Bone w={hero && i === 0 ? 150 : 96} h={hero && i === 0 ? 38 : 24} />
          <Bone w="62%" h={10} className="mt-3" />
        </div>
      ))}
    </Tiles>
  );
}

/**
 * Mirrors Card, with a bone where the heading is.
 *
 * `flush` carries Card's own meaning: the body runs to the edges, which is what
 * a card wrapping a table does.
 */
export function CardSkeleton(
  { head = true, sub = true, flush, className, children }:
  { head?: boolean; sub?: boolean; flush?: boolean; className?: string; children: ReactNode },
) {
  return (
    <section className={cx('bg-card border border-rule rounded shadow-sm', flush ? '' : 'p-5', className)}>
      {head && (
        <div className={cx('mb-3.5', flush && 'p-5 pb-0')}>
          <Bone w={168} h={15} />
          {sub && <Bone w="min(380px, 72%)" h={11} className="mt-2.5" />}
        </div>
      )}
      {children}
    </section>
  );
}

// ── content shapes ────────────────────────────────────────────

/**
 * Mirrors a record table, header included.
 *
 * Built from the real Table, Th and Td, so it stacks into cards on a phone
 * exactly as the loaded table does — a skeleton that kept its columns there
 * would promise the wrong thing.
 */
export function TableSkeleton(
  { rows = 6, cols = 5, cards = true, head = true }:
  { rows?: number; cols?: number; cards?: boolean; head?: boolean },
) {
  const widths = ['72%', '54%', '46%', '60%', '38%', '50%', '44%'];
  return (
    <TableWrap>
      <Table cards={cards}>
        {head && (
          <thead>
            <tr>
              {Array.from({ length: cols }, (_, c) => (
                <Th key={c} n={c >= cols - 2}><Bone w={c === 0 ? 78 : 56} h={9} /></Th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {Array.from({ length: rows }, (_, r) => (
            <tr key={r}>
              {Array.from({ length: cols }, (_, c) => (
                // A labelless first cell becomes the row's title once stacked,
                // the same arrangement the real rows use.
                <Td key={c} label={c === 0 ? '' : ' '} n={c >= cols - 2}>
                  <Bone w={widths[(r + c) % widths.length]} h={11} />
                </Td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </TableWrap>
  );
}

/** Mirrors RankedBars: a label column, a bar, and a figure at the right. */
export function BarsSkeleton({ rows = 6, labelWidth = 210 }: { rows?: number; labelWidth?: number }) {
  return (
    <div className="py-1">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3 h-[38px]">
          <span className="hidden sm:block shrink-0" style={{ width: labelWidth }}>
            <Bone w={(92 - i * 7) + '%'} h={11} />
          </span>
          <span className="flex-1 min-w-0">
            <Bone h={17} w={Math.max(14, 94 - i * 13) + '%'} radius={2} />
          </span>
          <span className="shrink-0 w-[78px] sm:w-[122px] flex justify-end">
            <Bone w={i % 2 ? 58 : 72} h={11} />
          </span>
        </div>
      ))}
    </div>
  );
}

/** Mirrors StackedColumns: gridlines, and columns of differing height. */
export function ColumnsSkeleton({ bars = 12 }: { bars?: number }) {
  const heights = [46, 68, 55, 82, 61, 74, 39, 88, 57, 70, 64, 79];
  return (
    <div className="relative h-[260px] sm:h-[330px] pt-6 pb-8 pl-9 sm:pl-13 pr-3">
      <div aria-hidden="true" className="absolute inset-x-3 top-6 bottom-8 flex flex-col justify-between">
        {Array.from({ length: 5 }, (_, i) => <span key={i} className="border-t border-rule-soft" />)}
      </div>
      <div className="relative h-full flex items-end gap-[6px]">
        {Array.from({ length: bars }, (_, i) => (
          <span key={i} className="flex-1 flex flex-col justify-end h-full">
            <Bone h={heights[i % heights.length] + '%'} radius={2} />
          </span>
        ))}
      </div>
    </div>
  );
}

/** Mirrors Note: a heading and a line or two inside a bordered panel. */
export function NoteSkeleton({ lines = 2 }: { lines?: number }) {
  return (
    <div className="border border-rule rounded bg-card-2 px-4 py-3.5 mb-5">
      <Bone w={140} h={9} className="mb-2" />
      <Lines n={lines} />
    </div>
  );
}

/** Mirrors FormGrid and Field, for the screens that open on a form. */
export function FormSkeleton({ fields = 6 }: { fields?: number }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {Array.from({ length: fields }, (_, i) => (
        <div key={i}>
          <Bone w={88} h={9} className="mb-2" />
          <Bone h={36} radius={6} />
        </div>
      ))}
    </div>
  );
}
